import jsPDF from 'jspdf';
import KitchenInvoice from '../components/invoices/KitchenInvoice';
import CustomerInvoice from '../components/invoices/CustomerInvoice';
import ReactDOMServer from 'react-dom/server';

// Import autoTable plugin
let autoTable;
try {
  autoTable = require('jspdf-autotable').default;
} catch (e) {
  try {
    autoTable = require('jspdf-autotable');
  } catch (e2) {
    console.error('Failed to load jspdf-autotable:', e2);
    autoTable = null;
  }
}

class MenuCardService {
  /**
   * Generate Menu Card PDF for public viewing
   * @param {Object} outlet - Outlet information
   * @param {Array} menuItems - Array of menu items
   * @param {Array} categories - Array of menu categories
   * @returns {Promise<Blob>} PDF blob
   */
  async generateMenuCardPDF(outlet, menuItems, categories) {
    try {
      console.log('Generating menu card PDF for outlet:', outlet);
      console.log('Menu items:', menuItems);
      console.log('Categories:', categories);

      // Validate inputs
      if (!outlet) {
        throw new Error('Outlet information is required');
      }

      if (!menuItems || !Array.isArray(menuItems)) {
        throw new Error('Menu items must be an array');
      }

      if (!categories || !Array.isArray(categories)) {
        console.warn('Categories not provided or not an array, using empty array');
        categories = [];
      }

      // Filter out invalid menu items
      const validMenuItems = menuItems.filter(item => {
        if (!item || typeof item !== 'object') {
          console.warn('Skipping invalid menu item:', item);
          return false;
        }
        if (!item.name || typeof item.name !== 'string') {
          console.warn('Skipping menu item without valid name:', item);
          return false;
        }
        return true;
      });

      console.log('Valid menu items:', validMenuItems.length);

      // Validate menu items structure for debugging
      this.validateMenuItems(validMenuItems);

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Set font
      doc.setFont('helvetica');
      
      // Header - Restaurant/Outlet Name
      doc.setFontSize(24);
      doc.setTextColor(25, 118, 210);
      doc.text(outlet?.name || 'Restaurant Menu', pageWidth / 2, 30, { align: 'center' });
      
      // Subtitle
      doc.setFontSize(16);
      doc.setTextColor(100, 100, 100);
      doc.text('Menu Card', pageWidth / 2, 40, { align: 'center' });
      
      // Outlet Information
      if (outlet?.address || outlet?.phone) {
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        if (outlet?.address) {
          doc.text(outlet.address, pageWidth / 2, 50, { align: 'center' });
        }
        if (outlet?.phone) {
          doc.text(`Phone: ${outlet.phone}`, pageWidth / 2, 55, { align: 'center' });
        }
      }
      
      // Line separator
      doc.setDrawColor(200, 200, 200);
      doc.line(20, 65, pageWidth - 20, 65);
      
      let yPosition = 80;
      
      // Check if we have any valid menu items
      if (validMenuItems.length === 0) {
        throw new Error('No valid menu items found to generate PDF');
      }

      // Group menu items by category
      const itemsByCategory = this.groupMenuItemsByCategory(validMenuItems, categories);
      
      // Generate menu for each category
      for (const [categoryName, items] of Object.entries(itemsByCategory)) {
        // Check if we need a new page
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = 30;
        }
        
        // Category Header
        doc.setFontSize(18);
        doc.setTextColor(25, 118, 210);
        doc.setFont('helvetica', 'bold');
        doc.text(categoryName.toUpperCase(), 20, yPosition);
        yPosition += 15;
        
        // Line under category
        doc.setDrawColor(25, 118, 210);
        doc.line(20, yPosition - 5, pageWidth - 20, yPosition - 5);
        yPosition += 10;
        
        // Menu Items for this category
        for (const item of items) {
          // Skip invalid items
          if (!item || !item.name) {
            console.warn('Skipping invalid menu item:', item);
            continue;
          }

          // Check if we need a new page
          if (yPosition > pageHeight - 40) {
            doc.addPage();
            yPosition = 30;
          }
          
          // Item Name
          doc.setFontSize(14);
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'bold');
          doc.text(item.name || 'Unknown Item', 20, yPosition);
          
          // Price - Handle different price structures
          let priceText = 'Price on request';
          if (item.price !== undefined && item.price !== null) {
            const price = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
            priceText = `S$${price.toFixed(2)}`;
          } else if (item.base_price !== undefined && item.base_price !== null) {
            const price = typeof item.base_price === 'number' ? item.base_price : parseFloat(item.base_price) || 0;
            priceText = `S$${price.toFixed(2)}`;
          } else if (item.sizes && Array.isArray(item.sizes) && item.sizes.length > 0) {
            // If no base price but has sizes, show "From S$X.XX" with lowest size price
            const sizePrices = item.sizes
              .map(size => size.price || size.additional_price || 0)
              .filter(price => price > 0);
            if (sizePrices.length > 0) {
              const minPrice = Math.min(...sizePrices);
              priceText = `From S$${minPrice.toFixed(2)}`;
            }
          }
          
          const priceWidth = doc.getTextWidth(priceText);
          doc.text(priceText, pageWidth - 20 - priceWidth, yPosition);
          
          yPosition += 8;
          
          // Description
          if (item.description && typeof item.description === 'string') {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            
            // Split long descriptions into multiple lines
            const maxWidth = pageWidth - 40;
            const descriptionLines = doc.splitTextToSize(item.description, maxWidth);
            
            for (const line of descriptionLines) {
              if (yPosition > pageHeight - 30) {
                doc.addPage();
                yPosition = 30;
              }
              doc.text(line, 20, yPosition);
              yPosition += 5;
            }
          }

          // Addons with prices
          if (item.addons && Array.isArray(item.addons) && item.addons.length > 0) {
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(80, 80, 80);
            doc.text('Add-ons:', 20, yPosition);
            yPosition += 5;
            
            for (const addon of item.addons) {
              if (yPosition > pageHeight - 30) {
                doc.addPage();
                yPosition = 30;
              }
              
              const addonName = addon.name || 'Unknown Add-on';
              const addonPrice = addon.price || addon.additional_price || 0;
              const addonPriceText = addonPrice > 0 ? `+S$${addonPrice.toFixed(2)}` : 'Free';
              
              doc.text(`  • ${addonName}`, 25, yPosition);
              const addonPriceWidth = doc.getTextWidth(addonPriceText);
              doc.text(addonPriceText, pageWidth - 20 - addonPriceWidth, yPosition);
              yPosition += 4;
            }
            yPosition += 3;
          }

          // Sizes with prices
          if (item.sizes && Array.isArray(item.sizes) && item.sizes.length > 0) {
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(80, 80, 80);
            doc.text('Sizes:', 20, yPosition);
            yPosition += 5;
            
            for (const size of item.sizes) {
              if (yPosition > pageHeight - 30) {
                doc.addPage();
                yPosition = 30;
              }
              
              const sizeName = size.name || size.size || 'Unknown Size';
              const sizePrice = size.price || size.additional_price || 0;
              const sizePriceText = sizePrice > 0 ? `+S$${sizePrice.toFixed(2)}` : 'Free';
              
              doc.text(`  • ${sizeName}`, 25, yPosition);
              const sizePriceWidth = doc.getTextWidth(sizePriceText);
              doc.text(sizePriceText, pageWidth - 20 - sizePriceWidth, yPosition);
              yPosition += 4;
            }
            yPosition += 3;
          }
          
          // Availability status
          if (item.is_available !== undefined) {
            doc.setFontSize(9);
            doc.setFont('helvetica', 'italic');
            if (item.is_available) {
              doc.setTextColor(0, 150, 0);
              doc.text('✓ Available', 20, yPosition);
            } else {
              doc.setTextColor(200, 0, 0);
              doc.text('✗ Currently Unavailable', 20, yPosition);
            }
          }
          
          yPosition += 12;
          
          // Add some space between items
          if (yPosition < pageHeight - 20) {
            yPosition += 5;
          }
        }
        
        // Add space between categories
        yPosition += 10;
      }
      
      // Footer
      yPosition = Math.max(yPosition, pageHeight - 30);
      doc.setDrawColor(200, 200, 200);
      doc.line(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'italic');
      doc.text('Thank you for choosing us!', pageWidth / 2, yPosition, { align: 'center' });
      doc.text(`Menu generated on ${new Date().toLocaleDateString('en-SG')}`, pageWidth / 2, yPosition + 5, { align: 'center' });
      
      return doc.output('blob');
    } catch (error) {
      console.error('Error generating menu card PDF:', error);
      throw error;
    }
  }

  /**
   * Group menu items by category
   * @param {Array} menuItems - Array of menu items
   * @param {Array} categories - Array of categories
   * @returns {Object} Grouped menu items by category
   */
  groupMenuItemsByCategory(menuItems, categories) {
    const grouped = {};
    
    // Initialize categories
    categories.forEach(category => {
      grouped[category.name] = [];
    });
    
    // Add "Other" category for items without category
    grouped['Other'] = [];
    
    // Group items by category
    menuItems.forEach(item => {
      const categoryName = item.category?.name || 'Other';
      if (grouped[categoryName]) {
        grouped[categoryName].push(item);
      } else {
        grouped['Other'].push(item);
      }
    });
    
    // Remove empty categories
    Object.keys(grouped).forEach(category => {
      if (grouped[category].length === 0) {
        delete grouped[category];
      }
    });
    
    return grouped;
  }

  /**
   * Download PDF file
   * @param {Blob} pdfBlob - PDF blob
   * @param {string} filename - Filename for download
   */
  downloadPDF(pdfBlob, filename) {
    try {
      console.log('Downloading PDF:', filename);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('PDF download completed');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }

  /**
   * Test function to validate menu item structure
   * @param {Array} menuItems - Array of menu items to validate
   */
  validateMenuItems(menuItems) {
    console.log('Validating menu items structure...');
    
    if (!Array.isArray(menuItems)) {
      console.error('Menu items is not an array:', typeof menuItems);
      return false;
    }

    menuItems.forEach((item, index) => {
      console.log(`Item ${index}:`, {
        name: item.name,
        price: item.price,
        base_price: item.base_price,
        hasAddons: !!(item.addons && item.addons.length > 0),
        hasSizes: !!(item.sizes && item.sizes.length > 0),
        addonCount: item.addons ? item.addons.length : 0,
        sizeCount: item.sizes ? item.sizes.length : 0,
        allKeys: Object.keys(item || {})
      });
    });

    return true;
  }
}

export default new MenuCardService();
